import React, { useState, useEffect, useRef } from 'react';
import { Plus, Upload, Download, Search, Edit2, Trash2, X, Check, Tv, RotateCcw, ArrowUpDown, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const GENRES = ['News','Entertainment','Movies','Music','Sports','Kids','Devotional','Infotainment','Lifestyle','Business','Educational','Local TV','Star','ZEE55','Other'];
const LANGUAGES = ['Malayalam','Tamil','Telugu','Kannada','Hindi','English','Bengali','Marathi','Gujarati','Punjabi','Odia','Assamese','Bhojpuri','Urdu','French','Nepali','Other'];

const SortableItem = ({ id, label }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '10px 14px', marginBottom: '6px',
        background: isDragging ? 'rgba(79,110,247,0.2)' : 'var(--bg-hover)',
        border: `1px solid ${isDragging ? 'var(--accent-blue)' : 'var(--border)'}`,
        borderRadius: '8px', cursor: 'grab', userSelect: 'none',
        opacity: isDragging ? 0.8 : 1,
      }}
      {...attributes} {...listeners}
    >
      <GripVertical size={16} color="var(--text-secondary)" />
      <span style={{ fontSize: '14px', fontWeight: '500' }}>{label}</span>
    </div>
  );
};

const Channels = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const isDistributor = user?.role === 'distributor';

  const [channels, setChannels] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [editChannel, setEditChannel] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editingLcn, setEditingLcn] = useState(null);
  const [lcnValue, setLcnValue] = useState('');
  const [filterLang, setFilterLang] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [langOrder, setLangOrder] = useState(LANGUAGES);
  const [genreOrder, setGenreOrder] = useState(GENRES);
  const [sortTab, setSortTab] = useState('language');
  const [applyingSort, setApplyingSort] = useState(false);
  const fileRef = useRef();
  const lcnInputRef = useRef();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const emptyForm = { name: '', lcn: '', language: 'Malayalam', genre: 'Entertainment', stream_url: '', backup_url: '', logo_url: '', stream_type: 'hls', is_adult: false };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchChannels(); }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(channels.filter(c =>
      (c.name?.toLowerCase().includes(q) || c.language?.toLowerCase().includes(q) || c.genre?.toLowerCase().includes(q)) &&
      (filterLang ? c.language === filterLang : true) &&
      (filterGenre ? c.genre === filterGenre : true)
    ));
  }, [search, channels, filterLang, filterGenre]);

  useEffect(() => {
    if (editingLcn && lcnInputRef.current) lcnInputRef.current.focus();
  }, [editingLcn]);

  const fetchChannels = async () => {
    try {
      const endpoint = isSuperAdmin ? '/api/channels' : '/api/channels/order/distributor';
      const res = await api.get(endpoint);
      setChannels(res.data.data || []);
    } catch { toast.error('Failed to load channels'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editChannel) {
        await api.put(`/api/channels/${editChannel.id}`, form);
        toast.success('Channel updated');
      } else {
        await api.post('/api/channels', form);
        toast.success('Channel added');
      }
      setShowAdd(false); setEditChannel(null); setForm(emptyForm);
      fetchChannels();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
  };

  const handleExport = () => {
    const headers = isSuperAdmin
      ? ['name','lcn','language','genre','stream_url','backup_url','logo_url','stream_type','is_adult']
      : ['name','lcn','language','genre'];
    const rows = channels.map(ch => headers.map(h => {
      const val = ch[h] ?? '';
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
    }).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `novacast-channels-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleEdit = (ch) => { setEditChannel(ch); setForm({ ...ch }); setShowAdd(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this channel?')) return;
    try { await api.delete(`/api/channels/${id}`); toast.success('Deleted'); fetchChannels(); }
    catch { toast.error('Failed to delete'); }
  };

  const startLcnEdit = (ch) => { setEditingLcn(ch.id); setLcnValue(ch.lcn || ''); };

  const saveLcn = async (id) => {
    if (!lcnValue) { setEditingLcn(null); return; }
    try {
      const res = await api.patch(`/api/channels/${id}/lcn`, { lcn: parseInt(lcnValue) });
      const msg = res.data?.swapped ? `LCN swapped with existing channel` : `LCN updated`; toast.success(msg);
      setChannels(prev => prev.map(c => c.id === id ? { ...c, lcn: parseInt(lcnValue) } : c));
    } catch { toast.error('Failed to update LCN'); }
    setEditingLcn(null);
  };

  const handleLcnKeyDown = (e, id) => {
    if (e.key === 'Enter') saveLcn(id);
    if (e.key === 'Escape') setEditingLcn(null);
  };

  const handleResetOrder = async () => {
    if (!window.confirm('Reset your custom channel order back to default?')) return;
    try { await api.delete('/api/channels/order/reset'); toast.success('Order reset'); fetchChannels(); }
    catch { toast.error('Failed to reset'); }
  };

  const handleDragEnd = (event, setter) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setter(items => arrayMove(items, items.indexOf(active.id), items.indexOf(over.id)));
    }
  };

  const applySort = async () => {
    setApplyingSort(true);
    try {
      // Sort only non-adult live channels
      const liveChannels = channels.filter(c => !c.is_adult);
      const sorted = [...liveChannels].sort((a, b) => {
        const li = langOrder.indexOf(a.language ?? 'Other');
        const lj = langOrder.indexOf(b.language ?? 'Other');
        if (li !== lj) return (li === -1 ? 999 : li) - (lj === -1 ? 999 : lj);
        const gi = genreOrder.indexOf(a.genre ?? 'Other');
        const gj = genreOrder.indexOf(b.genre ?? 'Other');
        if (gi !== gj) return (gi === -1 ? 999 : gi) - (gj === -1 ? 999 : gj);
        return (a.name || '').localeCompare(b.name || '');
      });

      // Batch update LCNs for live channels only (adult channels keep their existing LCNs)
      const updates = sorted.map((ch, idx) => api.patch(`/api/channels/${ch.id}/lcn`, { lcn: idx + 1 }));
      await Promise.all(updates);
      toast.success(`Channel order applied — ${sorted.length} channels renumbered`);
      setShowSort(false);
      fetchChannels();
    } catch { toast.error('Failed to apply sort'); }
    finally { setApplyingSort(false); }
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    return lines.slice(1).map(line => {
      const values = []; let current = ''; let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') inQuotes = !inQuotes;
        else if (line[i] === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
        else current += line[i];
      }
      values.push(current.trim());
      const obj = {}; headers.forEach((h, i) => { obj[h] = values[i] || ''; }); return obj;
    });
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const text = await file.text();
      let chs = file.name.endsWith('.csv') ? parseCSV(text) : (() => { const d = JSON.parse(text); return Array.isArray(d) ? d : (d.data || d.channels || []); })();
      if (!chs.length) { toast.error('No data found'); return; }
      toast.loading(`Importing ${chs.length} channels...`, { id: 'import' });
      const res = await api.post('/api/channels/bulk', { channels: chs });
      toast.dismiss('import'); setImportResult(res.data);
      toast.success(`Imported ${res.data.inserted} channels`); fetchChannels();
    } catch { toast.dismiss('import'); toast.error('Import failed'); }
    finally { setImporting(false); fileRef.current.value = ''; }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Channels</div>
          <div className="page-subtitle">{channels.length} channels total</div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {(isSuperAdmin || isDistributor) && (
            <button className="btn btn-secondary" onClick={() => setShowSort(true)}>
              <ArrowUpDown size={16} /> Sort Channels
            </button>
          )}
          {isDistributor && (
            <button className="btn btn-secondary" onClick={handleResetOrder}>
              <RotateCcw size={16} /> Reset Order
            </button>
          )}
          {isSuperAdmin && (
            <>
              <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
                <Upload size={16} /> Bulk Import
              </button>
              <button className="btn btn-primary" onClick={() => { setShowAdd(true); setEditChannel(null); setForm(emptyForm); }}>
                <Plus size={16} /> Add Channel
              </button>
            </>
          )}
          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
          <Search size={16} color="var(--text-secondary)" />
          <input placeholder="Search by name, language, genre..." value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={14} /></button>}
        </div>
        <select className="form-control" style={{ width: 'auto' }} value={filterLang} onChange={e => setFilterLang(e.target.value)}>
          <option value="">All Languages</option>
          {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select className="form-control" style={{ width: 'auto' }} value={filterGenre} onChange={e => setFilterGenre(e.target.value)}>
          <option value="">All Genres</option>
          {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="loading"><div className="spinner" />Loading channels...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><Tv size={48} /><p>No channels found</p></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>LCN {isDistributor && <span style={{ fontSize: '11px', color: 'var(--accent-teal)' }}>(your order)</span>}</th>
                  <th>Channel Name</th>
                  <th>Language</th>
                  <th>Genre</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(ch => (
                  <tr key={ch.id}>
                    <td>
                      {ch.logo_url ? (
                        <img src={ch.logo_url} alt={ch.name} style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '6px', background: '#fff1' }} onError={e => e.target.style.display='none'} />
                      ) : (
                        <div style={{ width: '36px', height: '36px', background: 'var(--bg-hover)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Tv size={16} color="var(--text-secondary)" />
                        </div>
                      )}
                    </td>
                    <td>
                      {editingLcn === ch.id ? (
                        <input ref={lcnInputRef} type="number" value={lcnValue}
                          onChange={e => setLcnValue(e.target.value)}
                          onKeyDown={e => handleLcnKeyDown(e, ch.id)}
                          onBlur={() => saveLcn(ch.id)}
                          style={{ width: '70px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--accent-teal)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '13px' }}
                        />
                      ) : (
                        <span onClick={() => (isSuperAdmin || isDistributor) && startLcnEdit(ch)}
                          title="Click to edit LCN"
                          style={{
                            fontFamily: 'monospace', fontSize: '13px',
                            cursor: (isSuperAdmin || isDistributor) ? 'pointer' : 'default',
                            padding: '2px 6px', borderRadius: '4px',
                            background: ch.custom_order ? 'rgba(0,212,170,0.15)' : 'transparent',
                            color: ch.custom_order ? 'var(--accent-teal)' : 'inherit',
                            border: (isSuperAdmin || isDistributor) ? '1px dashed var(--border)' : 'none',
                          }}>
                          {ch.lcn || '—'}
                        </span>
                      )}
                    </td>
                    <td style={{ fontWeight: '500' }}>{ch.name}</td>
                    <td>{ch.language || '—'}</td>
                    <td><span className="badge badge-blue">{ch.genre || '—'}</span></td>
                    <td><span className="badge badge-orange">{ch.stream_type?.toUpperCase()}</span></td>
                    <td><span className={`badge ${ch.is_active ? 'badge-success' : 'badge-danger'}`}>{ch.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      {isSuperAdmin && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => handleEdit(ch)} className="btn btn-secondary" style={{ padding: '6px 10px' }}><Edit2 size={14} /></button>
                          <button onClick={() => handleDelete(ch.id)} className="btn btn-danger" style={{ padding: '6px 10px' }}><Trash2 size={14} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sort Modal */}
      {showSort && (
        <div className="modal-overlay" onClick={() => setShowSort(false)}>
          <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Sort Channels</div>
              <button className="close-btn" onClick={() => setShowSort(false)}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Drag to reorder languages and genres. Click <strong>Apply</strong> to renumber all channels accordingly.
            </p>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {['language','genre'].map(tab => (
                <button key={tab} onClick={() => setSortTab(tab)}
                  className={sortTab === tab ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ textTransform: 'capitalize' }}>
                  {tab} Order
                </button>
              ))}
            </div>

            {sortTab === 'language' ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={e => handleDragEnd(e, setLangOrder)}>
                <SortableContext items={langOrder} strategy={verticalListSortingStrategy}>
                  <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                    {langOrder.map(lang => <SortableItem key={lang} id={lang} label={lang} />)}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={e => handleDragEnd(e, setGenreOrder)}>
                <SortableContext items={genreOrder} strategy={verticalListSortingStrategy}>
                  <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                    {genreOrder.map(genre => <SortableItem key={genre} id={genre} label={genre} />)}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button className="btn btn-secondary" onClick={() => setShowSort(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={applySort} disabled={applyingSort}>
                {applyingSort ? 'Applying...' : <><Check size={16} /> Apply Sort</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAdd && isSuperAdmin && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editChannel ? 'Edit Channel' : 'Add Channel'}</div>
              <button className="close-btn" onClick={() => setShowAdd(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>Channel Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="e.g. Star Plus HD" />
                </div>
                <div className="form-group">
                  <label>LCN</label>
                  <input className="form-control" type="number" value={form.lcn} onChange={e => setForm({...form, lcn: e.target.value})} placeholder="101" />
                </div>
                <div className="form-group">
                  <label>Stream Type</label>
                  <select className="form-control" value={form.stream_type} onChange={e => setForm({...form, stream_type: e.target.value})}>
                    <option value="hls">HLS</option>
                    <option value="udp">UDP</option>
                    <option value="mpegts">MPEG-TS</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Language</label>
                  <select className="form-control" value={form.language} onChange={e => setForm({...form, language: e.target.value})}>
                    {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Genre</label>
                  <select className="form-control" value={form.genre} onChange={e => setForm({...form, genre: e.target.value})}>
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>Stream URL *</label>
                  <input className="form-control" value={form.stream_url} onChange={e => setForm({...form, stream_url: e.target.value})} required placeholder="https://..." />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>Backup URL</label>
                  <input className="form-control" value={form.backup_url || ''} onChange={e => setForm({...form, backup_url: e.target.value})} placeholder="https://... (optional)" />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>Logo URL</label>
                  <input className="form-control" value={form.logo_url || ''} onChange={e => setForm({...form, logo_url: e.target.value})} placeholder="https://..." />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="checkbox" id="adult" checked={form.is_adult || false} onChange={e => setForm({...form, is_adult: e.target.checked})} />
                  <label htmlFor="adult" style={{ marginBottom: 0, cursor: 'pointer' }}>Adult Content (18+)</label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={16} />{editChannel ? 'Update' : 'Add Channel'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && isSuperAdmin && (
        <div className="modal-overlay" onClick={() => setShowImport(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Bulk Import Channels</div>
              <button className="close-btn" onClick={() => setShowImport(false)}><X size={20} /></button>
            </div>
            <div style={{ border: '2px dashed var(--border)', borderRadius: '12px', padding: '40px', textAlign: 'center', cursor: 'pointer' }}
              onClick={() => fileRef.current.click()}>
              <Upload size={32} color="var(--accent-blue)" style={{ marginBottom: '12px' }} />
              <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '4px' }}>{importing ? 'Importing...' : 'Click to upload'}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>CSV or JSON files</div>
              <input ref={fileRef} type="file" accept=".json,.csv" style={{ display: 'none' }} onChange={handleImport} />
            </div>
            {importing && <div className="loading" style={{ marginTop: '16px' }}><div className="spinner" />Processing...</div>}
            {importResult && (
              <div style={{ background: 'rgba(0,212,170,0.1)', border: '1px solid var(--accent-teal)', borderRadius: '10px', padding: '16px', marginTop: '16px' }}>
                <div style={{ color: 'var(--accent-teal)', fontWeight: '600', marginBottom: '8px' }}>✅ Import Complete</div>
                <div style={{ fontSize: '14px', marginBottom: '4px' }}>Inserted: <strong style={{color:'var(--accent-teal)'}}>{importResult.inserted}</strong> / Total: <strong>{importResult.total}</strong></div>{importResult.duplicateNames?.length > 0 && <div style={{fontSize:'13px',color:'var(--accent-orange)',marginTop:'8px'}}><strong>Duplicate Names ({importResult.duplicateNames.length}):</strong><br/>{importResult.duplicateNames.join(', ')}</div>}{importResult.duplicateLcns?.length > 0 && <div style={{fontSize:'13px',color:'var(--accent-red)',marginTop:'8px'}}><strong>Duplicate LCNs ({importResult.duplicateLcns.length}):</strong><br/>{importResult.duplicateLcns.join(', ')}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Channels;
